import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Clock,
    RefreshCcw,
    Calendar,
    FileText,
    AlertCircle,
    CheckCircle2,
    XCircle,
    Activity,
    ArrowRight
} from 'lucide-react';
import { user } from '../services/api';
import { useToast } from '../context/ToastContext';

const Historial = () => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [filter, setFilter] = useState('all'); // all, exchange, ap, system
    const toast = useToast();
    const [visualLimit, setVisualLimit] = useState(3);
    const INITIAL_FETCH_LIMIT = 20; // Fetch more to ensure we have non-system items
    const LOAD_MORE_FETCH_LIMIT = 20;

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async (loadMore = false) => {
        try {
            if (loadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
                setOffset(0);
                setVisualLimit(3); // Reset visual limit on reload
            }

            const currentOffset = loadMore ? offset : 0;
            const currentLimit = loadMore ? LOAD_MORE_FETCH_LIMIT : INITIAL_FETCH_LIMIT;

            const data = await user.getHistorial(currentLimit, currentOffset);

            if (loadMore) {
                setHistory(prev => [...prev, ...data.items]);
            } else {
                setHistory(data.items);
            }

            setHasMore(data.hasMore);
            setTotal(data.total);
            setOffset(currentOffset + currentLimit);
        } catch (error) {
            console.error('Error loading history:', error);
            toast.error('Error al cargar el historial');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Group by Month and Year
    // Exclude system events from user view entirely
    // Filter valid items first
    const systemTypes = ['SYSTEM_ACTION', 'AUDIT'];
    const validItems = history.filter(item => {
        // Always hide system events from users
        if (systemTypes.includes(item.type)) return false;

        if (filter !== 'all') {
            const typeMap = {
                'exchange': ['EXCHANGE_REQUEST', 'EXCHANGE_RESPONSE'],
                'ap': ['AP_REQUEST']
            };
            if (!typeMap[filter]?.includes(item.type)) return false;
        }
        return true;
    });

    // Slice for display
    const displayedItems = validItems.slice(0, visualLimit);

    // Group by Month and Year
    const groupedHistory = displayedItems.reduce((acc, item) => {
        const date = new Date(item.date);
        const key = date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});

    const handleLoadMore = () => {
        const nextLimit = visualLimit + 10;
        setVisualLimit(nextLimit);

        // If we've exhausted our local valid items (or are close), fetch more from server
        if (validItems.length < nextLimit + 5 && hasMore) { // Buffer of 5
            loadHistory(true);
        }
    };

    const getIcon = (type, action) => {
        switch (type) {
            case 'EXCHANGE_REQUEST':
            case 'EXCHANGE_RESPONSE':
                return <RefreshCcw className="w-5 h-5 text-cyan-400" />;
            case 'AP_REQUEST':
                return <FileText className="w-5 h-5 text-purple-400" />;
            case 'SYSTEM_ACTION':
                return <Activity className="w-5 h-5 text-slate-400" />;
            default:
                return <Clock className="w-5 h-5 text-indigo-400" />;
        }
    };

    const getStatusColor = (status) => {
        if (!status) return 'border-slate-600 text-slate-400 bg-slate-800';
        switch (status.toLowerCase()) {
            case 'aceptada':
            case 'completado':
            case 'aprobada':
                return 'border-green-500/30 text-green-400 bg-green-500/10';
            case 'cancelada':
            case 'rechazada':
            case 'expirada':
                return 'border-red-500/30 text-red-400 bg-red-500/10';
            case 'pendiente':
            case 'abierta':
                return 'border-yellow-500/30 text-yellow-400 bg-yellow-500/10';
            default:
                return 'border-slate-600 text-slate-400 bg-slate-800';
        }
    };

    // Get display label for status badge
    const getStatusLabel = (status) => {
        if (!status) return '';
        switch (status.toLowerCase()) {
            case 'completado':
                return 'COMPLETADO';
            case 'cancelada':
                return 'CANCELADA';
            case 'expirada':
                return 'EXPIRADA';
            case 'abierta':
                return 'ABIERTA';
            default:
                return status.toUpperCase();
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] pt-20">
                <div className="w-10 h-10 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mb-4 shadow-[0_0_15px_rgba(6,182,212,0.5)]" />
                <p className="text-slate-400 text-sm font-medium animate-pulse">Cargando historial...</p>
            </div>
        );
    }

    // Helper to format system action details as readable text
    const formatSystemDetails = (item) => {
        if (!item.details) return null;
        const { before, after } = item.details;
        const changes = [];
        const turnoNames = { 1: 'Mañana', 2: 'Tarde', 3: 'Noche', 4: 'Partido' };

        // Helper to format a date safely
        const formatDate = (dateStr) => {
            if (!dateStr) return null;
            try {
                const date = new Date(dateStr);
                return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
            } catch (e) {
                return dateStr.split('T')[0];
            }
        };

        // Helper to extract readable info from a data object
        const extractInfo = (data, prefix = '') => {
            if (!data) return;
            if (data.fecha) changes.push(`${prefix}Fecha: ${formatDate(data.fecha)}`);
            if (data.turno_nombre) changes.push(`${prefix}Turno: ${data.turno_nombre}`);
            else if (data.turno_id && turnoNames[data.turno_id]) changes.push(`${prefix}Turno: ${turnoNames[data.turno_id]}`);
            if (data.motivo_nombre) changes.push(`${prefix}Motivo: ${data.motivo_nombre}`);
            else if (data.motivo_id) changes.push(`${prefix}Motivo aplicado`);
        };

        if (item.action === 'MODIFICAR' && before && after) {
            // Compare key fields and show changes
            if (before.turno_id !== after.turno_id) {
                changes.push(`Turno: ${turnoNames[before.turno_id] || before.turno_id} → ${after.turno_nombre || turnoNames[after.turno_id] || after.turno_id}`);
            }
            if (before.motivo_id !== after.motivo_id) {
                changes.push(`Motivo: ${before.motivo_nombre || 'Ninguno'} → ${after.motivo_nombre || 'Ninguno'}`);
            }
            if (before.fecha !== after.fecha) {
                changes.push(`Fecha: ${formatDate(before.fecha)} → ${formatDate(after.fecha)}`);
            }
            // If no specific changes detected, show generic message
            if (changes.length === 0) {
                changes.push('Datos del día actualizados');
            }
        } else if ((item.action === 'ELIMINAR' || item.action === 'Deleted') && before) {
            extractInfo(before);
            if (changes.length === 0) changes.push('Registro eliminado');
        } else if ((item.action === 'CREAR' || item.action === 'Created') && after) {
            extractInfo(after);
            if (changes.length === 0) changes.push('Nuevo registro creado');
        } else if (before || after) {
            // Fallback: try to extract any useful info from available data
            if (before) extractInfo(before, 'Antes: ');
            if (after) extractInfo(after, 'Después: ');
            if (changes.length === 0) {
                changes.push('Actualización del calendario');
            }
        }

        return changes.length > 0 ? changes : ['Cambio en el calendario'];
    };

    return (
        <div className="pb-4 pt-4 w-full overflow-x-hidden">
            <header className="mb-4 px-2">
                <div className="flex items-center justify-between mb-2">
                    <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                        Historial
                    </h1>
                    <div className="w-9 h-9 rounded-full bg-[var(--glass-bg)] flex items-center justify-center border border-[var(--glass-border)]">
                        <Clock className="w-4 h-4 text-cyan-400" />
                    </div>
                </div>
                <p className="text-[var(--text-muted)] text-xs">
                    Registro de tu actividad
                </p>
            </header>

            {/* Filters */}
            <div className="flex gap-1.5 mb-3 overflow-x-auto pb-2 px-2 scrollbar-hide">
                {[
                    { id: 'all', label: 'Todo' },
                    { id: 'exchange', label: 'Intercambios' },
                    { id: 'ap', label: 'Asuntos Propios' }
                ].map(f => (
                    <button
                        key={f.id}
                        onClick={() => setFilter(f.id)}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap transition-all
                            ${filter === f.id
                                ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                                : 'bg-[var(--glass-bg)] text-[var(--text-muted)] border border-[var(--glass-border)]'
                            }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="space-y-3 px-2">
                {Object.entries(groupedHistory).map(([month, items]) => (
                    <section key={month}>
                        <div className="sticky top-[70px] z-20 mb-3 backdrop-blur-xl bg-[var(--bg-primary)]/80 py-2 -mx-2 px-2 border-y border-[var(--glass-border)]">
                            <h2 className="text-[10px] font-black text-cyan-500 uppercase tracking-widest">
                                {month}
                            </h2>
                        </div>

                        {/* Removed vertical timeline for mobile - cleaner look */}

                        <div className="space-y-3">
                            {items.map((item, index) => (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="group"
                                >
                                    {/* Card - Full width for mobile */}
                                    <div className="bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] p-3 rounded-xl border border-[var(--glass-border)] transition-all duration-300 shadow-sm hover:shadow-xl">
                                        <div className="flex items-start gap-3 mb-2">
                                            {/* Icon inline */}
                                            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[var(--glass-bg)] border border-[var(--glass-border)] flex items-center justify-center">
                                                {getIcon(item.type, item.action)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start gap-2">
                                                    <h3 className="text-sm font-bold text-[var(--text-primary)] leading-tight">
                                                        {item.description}
                                                    </h3>
                                                    {item.details?.status && (
                                                        <span className={`flex-shrink-0 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide border ${getStatusColor(item.details.status)}`}>
                                                            {getStatusLabel(item.details.status)}
                                                        </span>
                                                    )}
                                                    {item.type === 'SYSTEM_ACTION' && (
                                                        <span className="flex-shrink-0 bg-slate-700/50 text-slate-400 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide border border-slate-600/50">
                                                            Sistema
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 text-[10px] text-[var(--text-muted)] font-black uppercase tracking-widest mt-1">
                                                    <span>{new Date(item.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' })}</span>
                                                    <span className="w-1 h-1 rounded-full bg-[var(--glass-border)]" />
                                                    <span>{new Date(item.date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Details */}
                                        <div className="text-sm mt-2">
                                            {item.type === 'EXCHANGE_REQUEST' && item.details && (
                                                <div className="bg-[var(--glass-bg)] rounded-xl p-3 border border-[var(--glass-border)] space-y-2">
                                                    {/* Intercambio completado - mostrar info rica */}
                                                    {item.details.resultado === 'completado' && item.details.compañero ? (
                                                        <>
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                                                                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Intercambiaste con</p>
                                                                    <p className="text-slate-200 font-semibold">{item.details.compañero}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-2">
                                                                <div className="flex-1 bg-cyan-500/10 rounded-lg p-2 text-center border border-cyan-500/20">
                                                                    <p className="text-[10px] text-cyan-400 uppercase">Tu día libre</p>
                                                                    <p className="text-slate-200 text-sm font-medium">
                                                                        {new Date(item.details.dia_libre).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                                                    </p>
                                                                </div>
                                                                <ArrowRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                                                <div className="flex-1 bg-purple-500/10 rounded-lg p-2 text-center border border-purple-500/20">
                                                                    <p className="text-[10px] text-purple-400 uppercase">Trabajaste</p>
                                                                    <p className="text-slate-200 text-sm font-medium">
                                                                        {new Date(item.details.dia_trabajado).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </>
                                                    ) : item.details.resultado === 'cancelado' ? (
                                                        // Solicitud cancelada
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                                                                <XCircle className="w-4 h-4 text-red-400" />
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-slate-500 uppercase tracking-wider">Día solicitado</p>
                                                                <p className="text-slate-300 font-medium line-through opacity-60">
                                                                    {new Date(item.details.day).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        // Solicitud abierta o creación
                                                        <>
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                                                    <Calendar className="w-4 h-4 text-blue-400" />
                                                                </div>
                                                                <div>
                                                                    <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider font-black">Día solicitado</p>
                                                                    <p className="text-[var(--text-primary)] font-bold">
                                                                        {new Date(item.details.day).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            {item.action === 'CREATE' && (
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                                                                        <RefreshCcw className="w-4 h-4 text-purple-400" />
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider font-black">Turno</p>
                                                                        <p className="text-[var(--text-primary)] font-bold">
                                                                            {item.details.shift === 'M' ? 'Mañana' : 'Tarde'}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}

                                            {/* Exchange Response - mostrar info rica si fue aceptada */}
                                            {item.type === 'EXCHANGE_RESPONSE' && item.details && (
                                                <div className="bg-[var(--glass-bg)] rounded-xl p-3 border border-[var(--glass-border)] space-y-2">
                                                    {item.details.resultado === 'completado' && item.details.compañero ? (
                                                        <>
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                                                                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider">Intercambiaste con</p>
                                                                    <p className="text-[var(--text-primary)] font-semibold">{item.details.compañero}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-2">
                                                                <div className="flex-1 bg-cyan-500/10 rounded-lg p-2 text-center border border-cyan-500/20">
                                                                    <p className="text-[10px] text-cyan-400 uppercase">Tu día libre</p>
                                                                    <p className="text-[var(--text-primary)] text-sm font-medium">
                                                                        {new Date(item.details.dia_libre).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                                                    </p>
                                                                </div>
                                                                <ArrowRight className="w-4 h-4 text-[var(--text-muted)] flex-shrink-0" />
                                                                <div className="flex-1 bg-purple-500/10 rounded-lg p-2 text-center border border-purple-500/20">
                                                                    <p className="text-[10px] text-purple-400 uppercase">Trabajaste</p>
                                                                    <p className="text-[var(--text-primary)] text-sm font-medium">
                                                                        {new Date(item.details.dia_trabajado).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                                                                <Clock className="w-4 h-4 text-yellow-400" />
                                                            </div>
                                                            <div>
                                                                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider font-black">Estado</p>
                                                                <p className="text-[var(--text-primary)] font-bold capitalize">{item.details.status}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {item.type === 'SYSTEM_ACTION' && item.details && (
                                                <div className="bg-[var(--glass-bg)] rounded-lg p-2 border border-[var(--glass-border)]">
                                                    <div className="text-xs space-y-1">
                                                        {(() => {
                                                            const actionLower = (item.action || '').toLowerCase();
                                                            const isDelete = actionLower.includes('elimin') || actionLower.includes('delete');
                                                            const isCreate = actionLower.includes('crear') || actionLower.includes('create');
                                                            const colorClass = isDelete ? 'text-red-400/90' : isCreate ? 'text-emerald-400/90' : 'text-[var(--text-primary)]';
                                                            const icon = isDelete ? '✕' : isCreate ? '✓' : '•';
                                                            const iconColor = isDelete ? 'text-red-500' : isCreate ? 'text-emerald-500' : 'text-cyan-400';

                                                            return (
                                                                <div className={colorClass}>
                                                                    {formatSystemDetails(item)?.map((change, idx) => (
                                                                        <div key={idx} className="flex items-center gap-2">
                                                                            <span className={iconColor}>{icon}</span>
                                                                            <span>{change}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </section>
                ))}

                {validItems.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center py-20 text-[var(--text-muted)]">
                        <div className="w-24 h-24 bg-[var(--glass-bg)] rounded-full flex items-center justify-center mb-6 shadow-xl border border-[var(--glass-border)] animate-pulse">
                            <Clock className="w-12 h-12 opacity-30 text-cyan-500" />
                        </div>
                        <h3 className="text-xl font-black text-[var(--text-primary)] mb-2">Sin actividad visible</h3>
                        <p className="text-sm opacity-60 max-w-xs text-center font-medium">Tus acciones recientes aparecerán aquí.</p>
                    </div>
                )}

                {/* Load More Button */}
                {(hasMore || validItems.length > visualLimit) && !loading && (
                    <div className="flex flex-col items-center mt-6 mb-4">
                        <p className="text-[var(--text-muted)] text-[10px] font-black uppercase tracking-widest mb-3">
                            Mostrando {displayedItems.length} registros recientes
                        </p>
                        <button
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm font-medium border border-slate-700 transition-all flex items-center gap-2 disabled:opacity-50"
                        >
                            {loadingMore ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
                                    Cargando...
                                </>
                            ) : (
                                <>Cargar más</>
                            )}
                        </button>
                    </div>
                )}

                {/* End of list indicator */}
                {!hasMore && validItems.length <= visualLimit && validItems.length > 0 && !loading && (
                    <p className="text-center text-[var(--text-muted)] text-[10px] font-black uppercase tracking-widest mt-6 mb-4">
                        — Fin del historial —
                    </p>
                )}
            </div>
        </div>
    );
};

export default Historial;
